import React from 'react';

/**
 *  @PROPS
 *  id: string //ID of the wrapper div.
 *  labelId: string //ID of the internal label element.
 *  inputId: string //ID of the internal input element.
 *  onChange: function //Callback function to run on change. Accepts parameter event.
 *  onChangeDelay: number //Number of milliseconds to delay onChange callback by. Will reset on new change events.
 *  className: string //Class(es) to apply to the wrapper div.
 *  labelClassName: string //Class(es) to apply to the internal label element.
 *  inputClassName: string//Class(es) to apply to the internal input element.
 *  type: string//HTML5 input type. Defaults to text.
 *  label: string//Label text to display inside input.
 *  style: object//React type styles to apply to the wrapper div.
 *  labelStyle: object//React type styles to apply to the internal label element.
 *  inputStyle: object//React type styles to apply to the internal input element.
 */

class FloatingLabel extends React.Component {
  /**
		Initializes state and binds methods
		@param {any[]} props
		@constructor
	*/
  constructor(props) {
    super(props);
    this.handleTextChange = this.handleTextChange.bind(this);

    const ID = this.props.id ?? `float-label`;

    this.state = {
      isActive: !!this.props.initialValue,
      text: this.props.initialValue ?? '',
      id: ID,
      labelId: this.props.labelId ?? `${ID}-label`,
      inputId: this.props.inputId ?? `${ID}-input`,
      queuedChangeTimeout: undefined,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.initialValue !== prevProps.initialValue) {
      this.setState({
        text: this.props.initialValue,
        isActive: !!this.props.initialValue,
      });
    }
  }

  /**
		Event callback for input changes.
		Updates state to comply with React standard
		controlled input elements
		@param {object} event
		@param {boolean} force
	*/
  handleTextChange(event, force = false) {
    this.setState({
      isActive: event.target.value !== '',
      text: event.target.value,
    });

    if (this.props.onChange) {
      if (this.props.onChangeDelay) {
        if (this.state.queuedChangeTimeout) {
          clearTimeout(this.state.queuedChangeTimeout);
        }

        if (force) {
          this.props.onChange(event);
        } else {
          const timeoutId = setTimeout(() => {
            this.props.onChange(event);
            this.setState({ queuedChangeTimeout: undefined });
          }, this.props.onChangeDelay);
          this.setState({ queuedChangeTimeout: timeoutId });
        }
      } else {
        this.props.onChange(event);
      }
    }
  }

  /**
		Renders a form input with all the required callback and id information
		nested within a div, and sibling to a label
	*/
  render() {
    const propDefault = {
      className: this.props.className ?? '',
      labelClassName: this.props.labelClassName ?? '',
      inputClassName: this.props.inputClassName ?? '',
      type: this.props.type ?? 'text',
      label: this.props.label ?? 'Floating Label',
      style: this.props.style ?? {},
      labelStyle: this.props.labelStyle ?? {},
      inputStyle: this.props.inputStyle ?? {},
    };

    return (
      <>
        <div className={`floating-label ${propDefault.className}`} id={this.state.id} style={propDefault.style}>
          <label
            id={this.state.labelId}
            className={`${propDefault.labelClassName}${this.state.isActive ? ' floating-label-active' : ''}`}
            style={propDefault.labelStyle}
          >
            {propDefault.label}
          </label>
          <input
            className={propDefault.inputClassName}
            id={this.state.inputId}
            type={propDefault.type}
            value={this.state.text}
            onChange={this.handleTextChange}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                this.handleTextChange(event, true);
              }
            }}
            style={propDefault.inputStyle}
          />
        </div>
      </>
    );
  }
}

export default FloatingLabel;
